import React, { useState, useEffect } from 'react';
import Pagination, { usePagination } from '../../components/ui/Pagination';
import EmptyState from '../../components/ui/EmptyState';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import UniversalNavbar from '../../components/ui/UniversalNavbar';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import authorReviewService from '../../services/authorReviewService';
import SubmissionReviewCard from './components/SubmissionReviewCard';
import FilterBar from './components/FilterBar';
import SortableTh, { useSort } from '../../components/ui/SortableTh';

const AuthorReviewResultsDashboard = () => {
  const { t } = useTranslation('reviews');
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [filteredSubmissions, setFilteredSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({
    status: 'all',
    conference: 'all',
    sortBy: 'date'
  });
  const [searchQuery, setSearchQuery] = useState('');
  // Column sorting replaces the old 'Sort by' button row.
  const { sort, toggle, sortRows } = useSort('submittedAt', 'desc');
  // Above every conditional return: hook order must not change between renders.
  const { pageRows, ...pager } = usePagination(filteredSubmissions, 25);
  // Authors accumulate papers across conferences; this list grows without bound.

  // Redirect to sign-in if not authenticated
  useEffect(() => {
    if (!user) {
      navigate('/user-sign-in');
    }
  }, [user, navigate]);

  // Load submissions with reviews
  useEffect(() => {
    if (user) {
      loadSubmissionsWithReviews();
    }
  }, [user]);

  // Apply filters and search
  useEffect(() => {
    applyFiltersAndSearch();
  }, [submissions, filters, searchQuery, sort]);

  const loadSubmissionsWithReviews = async () => {
    try {
      setLoading(true);
      setError('');

      const [submissionsData, statsData] = await Promise.all([
        authorReviewService?.getSubmissionsWithReviews(user?.id),
        authorReviewService?.getAuthorStats(user?.id)
      ]);

      setSubmissions(submissionsData);
      setStats(statsData);
    } catch (err) {
      console.error('Load submissions error:', err);
      setError(err?.message || t('authorResults.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const SORT_ACCESSORS = {
    score: (r) => r?.aggregateScores?.avgOverall ?? null,
    reviews: (r) => r?.reviews?.length ?? 0,
    conferenceName: (r) => r?.conferenceName || '',
  };

  const applyFiltersAndSearch = () => {
    let filtered = [...submissions];

    // Filter by status
    if (filters?.status !== 'all') {
      filtered = filtered?.filter(sub => sub?.status === filters?.status);
    }

    // Filter by conference
    if (filters?.conference !== 'all') {
      filtered = filtered?.filter(sub => sub?.conferenceId === filters?.conference);
    }

    // Search by title or keywords
    if (searchQuery?.trim()) {
      const query = searchQuery?.toLowerCase();
      filtered = filtered?.filter(sub => 
        sub?.title?.toLowerCase()?.includes(query) ||
        sub?.keywords?.some(kw => kw?.toLowerCase()?.includes(query))
      );
    }

    // Sorting is driven by the table headers via the shared comparator, which
    // handles nulls (an unscored paper sorts last, not first) and locale-aware
    // string ordering — neither of which the old inline sort did.
    setFilteredSubmissions(sortRows(filtered, SORT_ACCESSORS));
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({ ...prev, [filterType]: value }));
  };

  const handleRefresh = () => {
    loadSubmissionsWithReviews();
  };

  const getStatusBadgeColor = (status) => {
    const colors = {
      'submitted': 'bg-primary-subtle text-primary',
      'under_review': 'bg-warning-subtle text-warning',
      'accepted': 'bg-success-subtle text-success',
      'rejected': 'bg-error-subtle text-error',
      'revision_required': 'bg-accent-subtle text-accent'
    };
    return colors?.[status] || 'bg-muted text-foreground';
  };

  const formatStatus = (status) => {
    return status?.split('_')?.map(word => word?.charAt(0)?.toUpperCase() + word?.slice(1))?.join(' ');
  };

  // Get unique conferences for filter
  const uniqueConferences = [...new Set(submissions?.map(sub => ({
    id: sub?.conferenceId,
    name: sub?.conferenceName
  })))];

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <UniversalNavbar />
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="text-center">
            <Icon name="Loader" size={48} className="animate-spin text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">{t('authorResults.loading')}</p>
          </div>
        </div>
      </div>
    );
  }


  return (
    <div className="min-h-screen bg-background">
      <UniversalNavbar />
      <div className="page-shell py-8">
        {/* Page title. Previously the page opened straight into five counters
            with no heading at all — you had to infer where you were. */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-foreground tracking-tight">
            {t('authorResults.title', { defaultValue: 'Review results' })}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {t('authorResults.subtitle', { defaultValue: 'Decisions and reviewer feedback on your papers.' })}
          </p>
        </div>

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto p-4 md:p-6">
          {error && (
            <div className="bg-error/10 border border-error text-error px-4 py-3 rounded-lg mb-6 flex items-center gap-2">
              <Icon name="AlertCircle" size={20} />
              <span>{error}</span>
            </div>
          )}

          {/* Filter Bar */}
          <FilterBar
            filters={filters}
            onFilterChange={handleFilterChange}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            conferences={uniqueConferences}
            resultCount={filteredSubmissions?.length}
            totalCount={submissions?.length}
          />

          {/* Submissions List */}
          {filteredSubmissions?.length === 0 ? (
            (() => {
              const isFiltered = Boolean(searchQuery) || filters?.status !== 'all' || filters?.conference !== 'all';
              return (
                <EmptyState
                  filtered={isFiltered}
                  onClear={() => { setSearchQuery(''); setFilters({ ...filters, status: 'all', conference: 'all' }); }}
                  icon="FileText"
                  title={isFiltered ? t('authorResults.emptyState.adjustFilters') : t('authorResults.emptyState.noSubmissions')}
                  body={
                    isFiltered
                      ? 'No papers match the filters you have applied. Try widening them or clearing them entirely.'
                      : "Reviews land here once you've submitted a paper and the reviewers have scored it."
                  }
                  action={
                    isFiltered
                      ? undefined
                      : { label: 'Submit a paper', icon: 'Plus', onClick: () => navigate('/author-submission-portal') }
                  }
                />
              );
            })()
          ) : (
            <div className="bg-card rounded-lg border border-border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="table-clear">
                  <thead>
                    <tr>
                      {/* Sorting lives in the headers now. The separate
                          "Sort by" button row offered the same three orderings
                          in a second, competing control — same capability,
                          fewer things on screen. */}
                      <th className="col-primary">{t('authorResults.columns.title', { defaultValue: 'Paper Title' })}</th>
                      <SortableTh field="conferenceName" sort={sort} onSort={toggle}>
                        {t('authorResults.columns.conference', { defaultValue: 'Conference' })}
                      </SortableTh>
                      <SortableTh field="status" sort={sort} onSort={toggle}>
                        {t('authorResults.columns.status', { defaultValue: 'Status' })}
                      </SortableTh>
                      <SortableTh field="score" sort={sort} onSort={toggle} align="end">
                        {t('authorResults.columns.score', { defaultValue: 'Score' })}
                      </SortableTh>
                      <SortableTh field="reviews" sort={sort} onSort={toggle} align="end">
                        {t('authorResults.columns.reviews', { defaultValue: 'Reviews' })}
                      </SortableTh>
                      <SortableTh field="submittedAt" sort={sort} onSort={toggle} align="end">
                        {t('authorResults.columns.submitted', { defaultValue: 'Submitted' })}
                      </SortableTh>
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows?.map(submission => (
                      <SubmissionReviewCard
                        key={submission?.id}
                        submission={submission}
                        getStatusBadgeColor={getStatusBadgeColor}
                        formatStatus={formatStatus}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
              <Pagination {...pager} />
            </div>
          )}

          {/* Counts as context, under the work — not five cards in front of it.
              Only the figures implying an action (revision requested) are
              allowed to carry colour. */}
          {stats && (
            <section className="mt-6 border-t border-border pt-4">
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                {[
                  { label: t('authorResults.stats.totalSubmissions'), value: stats?.total, tone: 'text-foreground' },
                  { label: t('authorResults.stats.underReview'), value: stats?.underReview, tone: 'text-foreground' },
                  { label: t('authorResults.stats.accepted'), value: stats?.accepted, tone: 'text-foreground' },
                  { label: t('authorResults.stats.rejected'), value: stats?.rejected, tone: 'text-foreground' },
                ].map((s) => (
                  <div key={s.label} className="flex items-baseline gap-1.5">
                    <span className={`text-sm font-semibold tnum ${s.tone}`}>{s.value ?? 0}</span>
                    <span className="text-xs text-muted-foreground">{s.label}</span>
                  </div>
                ))}
                {stats?.revisionRequired > 0 && (
                  <div className="flex items-baseline gap-1.5">
                    <span className="text-sm font-semibold text-accent tnum">{stats.revisionRequired}</span>
                    <span className="text-xs text-accent">{t('authorResults.stats.revisionRequired')}</span>
                  </div>
                )}
              </div>
            </section>
          )}
        </main>
      </div>
    </div>
  );
};

export default AuthorReviewResultsDashboard;
